/**
 * DecisionFlow — OpsDashboard
 * ui/src/components/platform-admin/OpsDashboard.jsx
 * OI-SEAT-IMPLEMENTATION · S660
 *
 * Seat utilisation dashboard for tool_admin / platform_admin.
 * Fetches GET /api/v1/admin/ops/seat-utilisation
 *
 * Response shape (per tenant_admin_router.py):
 *   { tenants: [{ tenant_id, tenant_name, tenant_slug, subscription_tier,
 *                 tenant_status_code, is_demo, max_positions,
 *                 active_positions, utilisation_pct, is_overflow }] }
 *
 * Colour coding:
 *   overflow   (active > purchased)  → danger
 *   near-limit (utilisation >= 80%)  → warning
 *   healthy                          → success
 *
 * Design rules (§15):
 *   - No dark: prefix
 *   - All text sizes text-tok-*
 *   - rgb(var(--color-*)) throughout
 *   - .skeleton for loading states
 */

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, AlertTriangle, TrendingUp } from "lucide-react";

import Card from "../shared/Card.jsx";
import Button from "../shared/Button.jsx";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-2 py-2">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-10 w-full rounded skeleton" />
      ))}
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load seat utilisation data."}
    </div>
  );
}

function utilisationColour(pct, isOverflow) {
  if (isOverflow) return "text-[rgb(var(--color-danger))] font-semibold";
  if (pct >= 80)  return "text-[rgb(var(--color-warning))] font-medium";
  return "text-[rgb(var(--color-success))]";
}

function utilisationBarColour(pct, isOverflow) {
  if (isOverflow) return "bg-[rgb(var(--color-danger))]";
  if (pct >= 80)  return "bg-[rgb(var(--color-warning))]";
  return "bg-[rgb(var(--color-success))]";
}

function UtilisationBar({ pct, isOverflow }) {
  const capped = Math.min(pct ?? 0, 100);
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 rounded-full bg-[rgb(var(--color-surface-muted))]">
        <div
          className={`h-1.5 rounded-full transition-all ${utilisationBarColour(pct, isOverflow)}`}
          style={{ width: `${capped}%` }}
        />
      </div>
    </div>
  );
}

function OverflowBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-danger))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-danger))]">
      <AlertTriangle className="h-3 w-3" /> Overflow
    </span>
  );
}

function NearLimitBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">
      <TrendingUp className="h-3 w-3" /> Near limit
    </span>
  );
}

// ---------------------------------------------------------------------------
// Summary cards
// ---------------------------------------------------------------------------

function SummaryCard({ label, value, colour }) {
  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">
        {label}
      </p>
      <p className={`mt-1 text-tok-xl font-bold ${colour ?? "text-[rgb(var(--color-text))]"}`}>
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OpsDashboard
// ---------------------------------------------------------------------------

export default function OpsDashboard() {
  const [tenants,  setTenants]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [filter,   setFilter]   = useState("all"); // all | overflow | near | healthy

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/ops/seat-utilisation`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
      }
      const data = await res.json();
      setTenants(data.tenants ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Derived counts for summary
  const overflowCount  = tenants.filter((t) => t.is_overflow).length;
  const nearLimitCount = tenants.filter((t) => !t.is_overflow && (t.utilisation_pct ?? 0) >= 80).length;
  const healthyCount   = tenants.filter((t) => !t.is_overflow && (t.utilisation_pct ?? 0) < 80).length;

  // Apply filter
  const filtered = tenants.filter((t) => {
    if (filter === "overflow") return t.is_overflow;
    if (filter === "near")     return !t.is_overflow && (t.utilisation_pct ?? 0) >= 80;
    if (filter === "healthy")  return !t.is_overflow && (t.utilisation_pct ?? 0) < 80;
    return true;
  });

  const filterBtn = (key, label, count, activeColour) => (
    <button
      onClick={() => setFilter(key)}
      className={[
        "rounded-lg px-3 py-1.5 text-tok-xs font-medium transition",
        filter === key
          ? `${activeColour} text-white`
          : "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]",
      ].join(" ")}
    >
      {label} {count > 0 && <span className="ml-1 opacity-80">({count})</span>}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryCard label="Total tenants"  value={tenants.length} />
        <SummaryCard
          label="Overflow"
          value={overflowCount}
          colour={overflowCount > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-text))]"}
        />
        <SummaryCard
          label="Near limit ≥80%"
          value={nearLimitCount}
          colour={nearLimitCount > 0 ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text))]"}
        />
        <SummaryCard
          label="Healthy"
          value={healthyCount}
          colour="text-[rgb(var(--color-success))]"
        />
      </div>

      <Card
        title="Seat Utilisation"
        action={
          <Button variant="secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      >
        {/* Filter bar */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {filterBtn("all",      "All",        tenants.length, "bg-[rgb(var(--color-primary))]")}
          {filterBtn("overflow", "Overflow",   overflowCount,  "bg-[rgb(var(--color-danger))]")}
          {filterBtn("near",     "Near limit", nearLimitCount, "bg-[rgb(var(--color-warning))]")}
          {filterBtn("healthy",  "Healthy",    healthyCount,   "bg-[rgb(var(--color-success))]")}
        </div>

        {loading && <LoadingSkeleton />}
        {error   && <ErrorNote message={error} />}

        {!loading && !error && filtered.length === 0 && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No tenants match this filter.
          </p>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="overflow-auto rounded-xl border border-[rgb(var(--color-border))]"
               style={{ maxHeight: "calc(100vh - 380px)", minHeight: "280px" }}>
            <table className="w-full border-collapse text-tok-sm">
              <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface))]">
                <tr className="border-b border-[rgb(var(--color-border))]">
                  {[
                    "Tenant", "Tier", "Active", "Purchased", "Utilisation", "Status", "Flag",
                  ].map((h) => (
                    <th
                      key={h}
                      className="py-2 pr-4 text-left text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] whitespace-nowrap first:pl-3"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((tenant) => {
                  const pct        = tenant.utilisation_pct ?? 0;
                  const isOverflow = tenant.is_overflow ?? false;
                  return (
                    <tr
                      key={tenant.tenant_id}
                      className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))] transition"
                    >
                      {/* Tenant name — drill-down link */}
                      <td className="py-2.5 pl-3 pr-4 whitespace-nowrap">
                        <Link
                          to={`/platform-admin/tenants/${tenant.tenant_id}`}
                          className="font-medium text-[rgb(var(--color-primary))] hover:underline"
                        >
                          {tenant.tenant_name}
                        </Link>
                        {tenant.is_demo && (
                          <span className="ml-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                            demo
                          </span>
                        )}
                      </td>

                      {/* Tier */}
                      <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text))] whitespace-nowrap capitalize">
                        {tenant.subscription_tier ?? "—"}
                      </td>

                      {/* Active */}
                      <td className={`py-2.5 pr-4 text-tok-xs whitespace-nowrap ${utilisationColour(pct, isOverflow)}`}>
                        {tenant.active_positions ?? "—"}
                      </td>

                      {/* Purchased */}
                      <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text))] whitespace-nowrap">
                        {tenant.max_positions ?? "—"}
                      </td>

                      {/* Utilisation bar + % */}
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <UtilisationBar pct={pct} isOverflow={isOverflow} />
                          <span className={`text-tok-xs tabular-nums ${utilisationColour(pct, isOverflow)}`}>
                            {isOverflow ? `${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`}
                          </span>
                        </div>
                      </td>

                      {/* Tenant status */}
                      <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                        {tenant.tenant_status_code ?? "—"}
                      </td>

                      {/* Overflow / near-limit flag */}
                      <td className="py-2.5 pr-3 whitespace-nowrap">
                        {isOverflow
                          ? <OverflowBadge />
                          : pct >= 80
                          ? <NearLimitBadge />
                          : null
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && tenants.length > 0 && (
          <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Sorted by utilisation descending. Overflow tenants shown first.
            Data sourced from <span className="font-mono">tenant_usage_meter</span> — no position scan.
          </p>
        )}
      </Card>
    </div>
  );
}
